// src/pages/SettingsPage.js - 설정 페이지

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import WorkspaceLayout from '../components/WorkspaceLayout';
import { ConfirmModal } from '../components/ui';
import { useAppContext } from '../context/AppContext';
import { getProjects, deleteAccount } from '../services/api';
import { usePageHeader } from '../context/PageHeaderContext';
import './SettingsPage.scss';

const SettingsPage = () => {
  const { user, logoutUser } = useAppContext();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [projectCount, setProjectCount] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  usePageHeader(
    {
      title: '설정',
      subtitle: user?.email || '',
      showBack: true,
      onBack: () => navigate(-1),
    },
    [user?.email]
  );

  useEffect(() => {
    // 프로젝트 개수 가져오기
    const fetchProjectCount = async () => {
      try {
        const response = await getProjects();
        setProjectCount(response?.projects?.length || 0);
      } catch (error) {
        console.error('프로젝트 개수 가져오기 실패:', error);
        setProjectCount(0);
      }
    };
    fetchProjectCount();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      // 계정 삭제 API 호출
      const result = await deleteAccount();

      // 로그아웃 및 홈으로 이동
      logoutUser();
      navigate('/');
    } catch (error) {
      console.error('계정 삭제 실패:', error);
      const errorMessage = error.message || '계정 삭제에 실패했습니다.';
      alert(`계정 삭제 실패: ${errorMessage}\n\n다시 시도해주세요.`);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <WorkspaceLayout hideCollapseButton={true} defaultCollapsed={false}>
      <div className="settings-page-content">
        <header className="settings-header">
          <div>
            <h1>설정</h1>
            <p className="settings-subtitle">계정 정보 및 설정</p>
          </div>
        </header>

        <section className="settings-content">
          <div className="settings-section">
            <div className="settings-info-row">
              <div className="settings-label">회원 가입일</div>
              <div className="settings-value">{formatDate(user?.created_at || user?.createdAt)}</div>
            </div>
            {user?.account_type && (
              <div className="settings-info-row">
                <div className="settings-label">계정 유형</div>
                <div className="settings-value">
                  {user.account_type === 'business' ? '기업' : '개인'}
                </div>
              </div>
            )}
            {user?.tier && (
              <div className="settings-info-row">
                <div className="settings-label">요금제</div>
                <div className="settings-value">
                  {user.tier === 'free' ? 'Free' :
                   user.tier === 'basic' ? 'Basic' :
                   user.tier === 'premium' ? 'Premium' :
                   user.tier === 'enterprise' ? 'Enterprise' :
                   user.tier === 'admin' ? 'Admin' :
                   user.tier === 'super' ? 'Super' : user.tier}
                </div>
              </div>
            )}
            {user?.company_id && (
              <div className="settings-info-row">
                <div className="settings-label">회사 ID</div>
                <div className="settings-value">{user.company_id}</div>
              </div>
            )}
            {user?.department && (
              <div className="settings-info-row">
                <div className="settings-label">소속 부서</div>
                <div className="settings-value">{user.department}</div>
              </div>
            )}
          </div>

          <div className="settings-danger-zone">
            <div className="danger-zone-text">
              <h3>계정 삭제</h3>
              <p className="danger-zone-description">
                계정을 삭제하면 모든 데이터가 영구적으로 삭제되며 복구할 수 없습니다.
              </p>
            </div>
            <button 
              className="delete-account-btn"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <FontAwesomeIcon icon={faTrash} />
              계정 삭제
            </button>
          </div>
        </section>
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        type="error"
        title="계정 삭제 확인"
        message={`${projectCount}개의 프로젝트와 모든 데이터가 영구적으로 삭제됩니다.`}
        subMessage="이 작업은 되돌릴 수 없습니다."
        confirmText={isDeleting ? "삭제 중..." : "삭제"}
        cancelText="취소"
        showCancel={true}
        onConfirm={handleDeleteAccount}
        onCancel={() => setShowDeleteConfirm(false)}
        canCloseOutside={false}
      />
    </WorkspaceLayout>
  );
};

export default SettingsPage;
